import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Bot, ArrowRight, Sparkles, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import LanguageSelector from "@/components/LanguageSelector";
import { useLocalizedTo } from "@/hooks/useLocalePrefix";
import ThemeToggle from "@/components/ThemeToggle";
import StickyCTABar from "@/components/StickyCTABar";

const PageLayout = ({ children }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Keep the shared nav/footer links in the visitor's locale (see #614): a
  // Spanish visitor's "Pricing"/"Blog" must go to /es/pricing, not the FR root.
  const to = useLocalizedTo();

  return (
    <div className="min-h-screen bg-background">
      {/* Skip link — RGAA 12.7 / WCAG 2.4.1: lets keyboard & screen-reader
          users jump past the nav straight to the main content. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:px-4 focus:py-2 focus:rounded-md focus:bg-primary-strong focus:text-primary-strong-foreground focus:shadow-lg focus-visible:ring-2 focus-visible:ring-white"
      >
        {t("a11y.skipToContent", "Aller au contenu")}
      </a>
      <nav className="fixed top-0 left-0 right-0 z-40 glass safe-area-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to={to("/")} className="flex items-center gap-2 shrink-0">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center shrink-0">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <span className="font-heading font-bold text-xl text-white whitespace-nowrap">
                Stronger Bot
              </span>
            </Link>
            <div className="hidden md:flex items-center gap-6 text-sm">
              <Link
                to={to("/use-cases")}
                className="text-slate-400 hover:text-white transition-colors"
              >
                {t("nav.useCases")}
              </Link>
              <Link
                to={to("/blog")}
                className="text-slate-400 hover:text-white transition-colors"
              >
                {t("nav.blog")}
              </Link>
              <Link
                to={to("/pricing")}
                className="text-slate-400 hover:text-white transition-colors"
              >
                {t("nav.pricing")}
              </Link>
              <Link
                to={to("/about")}
                className="text-slate-400 hover:text-white transition-colors"
              >
                {t("nav.about")}
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <ThemeToggle />
              <LanguageSelector variant="ghost" />
              {isAuthenticated ? (
                <Link to="/dashboard" className="hidden sm:block">
                  <Button
                    className="btn-glow bg-primary-strong hover:bg-primary-strong/90"
                    size="sm"
                  >
                    {t("nav.dashboard")} <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              ) : (
                <Link to="/register" className="hidden sm:block">
                  <Button
                    className="btn-glow bg-primary-strong hover:bg-primary-strong/90"
                    size="sm"
                  >
                    {t("landing.cta")} <Sparkles className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              )}
              {/* Mobile menu toggle — the desktop nav links are md:hidden, so
                  without this there was no way to reach use-cases/blog/pricing/
                  about on a phone (§E mobile-first nav gap). */}
              <button
                type="button"
                onClick={() => setMobileOpen((v) => !v)}
                className="md:hidden inline-flex items-center justify-center w-11 h-11 -mr-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                aria-label={t("nav.menu", "Menu")}
                aria-expanded={mobileOpen}
                aria-controls="mobile-nav"
              >
                {mobileOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile nav panel — the missing half of the responsive header */}
        {mobileOpen && (
          <div
            id="mobile-nav"
            className="md:hidden border-t border-white/10 bg-background/95 backdrop-blur-xl"
          >
            <div className="px-4 py-3 space-y-1">
              {[
                { to: "/use-cases", label: t("nav.useCases") },
                { to: "/blog", label: t("nav.blog") },
                { to: "/pricing", label: t("nav.pricing") },
                { to: "/about", label: t("nav.about") },
                { to: "/models", label: t("nav.models", "Modèles IA") },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={to(item.to)}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center min-h-[44px] px-3 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to={isAuthenticated ? "/dashboard" : "/register"}
                onClick={() => setMobileOpen(false)}
                className="block pt-2"
              >
                <Button className="btn-glow w-full bg-primary-strong hover:bg-primary-strong/90">
                  {isAuthenticated ? t("nav.dashboard") : t("landing.cta")}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* pt-20 clears the 64px fixed nav; on notched iOS/PWA the nav grows by
          the safe-area inset (safe-area-top), so the main offset must grow with
          it or content underlaps the bar. env()-gated → 5rem on every other
          surface (no visual change). */}
      <main
        id="main-content"
        style={{ paddingTop: "calc(5rem + var(--safe-area-inset-top))" }}
      >
        {children}
      </main>
      <StickyCTABar />

      <footer className="py-12 px-4 border-t border-white/5 mt-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <span className="font-heading font-semibold text-white">
                  Stronger Bot
                </span>
              </div>
              <p className="text-slate-400 text-sm">{t("footer.tagline")}</p>
            </div>
            <div>
              <h4 className="text-white font-medium mb-3 text-sm">
                {t("footer.product")}
              </h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li>
                  <Link
                    to={to("/models")}
                    className="hover:text-white transition-colors"
                  >
                    {t("nav.models", "Modèles IA")}
                  </Link>
                </li>
                <li>
                  <Link
                    to={to("/use-cases")}
                    className="hover:text-white transition-colors"
                  >
                    {t("nav.useCases")}
                  </Link>
                </li>
                <li>
                  <Link
                    to={to("/kits")}
                    className="hover:text-white transition-colors"
                  >
                    {t("kits.badge", "Kits par métier")}
                  </Link>
                </li>
                <li>
                  <Link
                    to={to("/prompts")}
                    className="hover:text-white transition-colors"
                  >
                    {t("prompts.nav")}
                  </Link>
                </li>
                <li>
                  <Link
                    to={to("/pricing")}
                    className="hover:text-white transition-colors"
                  >
                    {t("nav.pricing")}
                  </Link>
                </li>
                <li>
                  <Link
                    to={to("/blog")}
                    className="hover:text-white transition-colors"
                  >
                    {t("nav.blog")}
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-medium mb-3 text-sm">
                {t("footer.company")}
              </h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li>
                  <Link
                    to={to("/about")}
                    className="hover:text-white transition-colors"
                  >
                    {t("nav.about")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="hover:text-white transition-colors"
                  >
                    {t("nav.contact")}
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-medium mb-3 text-sm">
                {t("footer.legal")}
              </h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li>
                  <Link
                    to="/privacy"
                    className="hover:text-white transition-colors"
                  >
                    {t("legal.nav.privacy")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="hover:text-white transition-colors"
                  >
                    {t("legal.nav.terms")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/legal"
                    className="hover:text-white transition-colors"
                  >
                    {t("legal.nav.legal")}
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/5 pt-6 text-center text-slate-400 text-sm">
            {t("landing.footer")}
          </div>
        </div>
      </footer>
    </div>
  );
};
export default PageLayout;
